import { motion } from "framer-motion";
import { Anchor, Download } from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import JumpingFish from "../components/JumpingFish";
import Bubbles from "../components/Bubbles";
import TimelineTrack from "../components/TimelineTrack";
import ValueCard from "../components/ValueCard";
import LeadershipGrid from "../components/LeadershipGrid";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePdfDownload from "../hooks/usePdfDownload";
import { MISSION, VISION, FOUNDER_STORY_CAPTION } from "../data/companyProfile";
import { TIMELINE } from "../data/timeline";
import { CORE_VALUES } from "../data/coreValues";

const FISHERMEN = [
  { initials: "RP", name: "Ramesh P.", region: "Konkan Coast", years: "18 yrs" },
  { initials: "SK", name: "Sunitha K.", region: "Malabar Coast", years: "12 yrs" },
  { initials: "AD", name: "Arun D.", region: "Coromandel Coast", years: "24 yrs" },
  { initials: "FS", name: "Fatima S.", region: "Konkan Coast", years: "9 yrs" },
];

export default function About() {
  useDocumentTitle("Our Story");
  const { status: pdfStatus, download: handleDownloadProfile } = usePdfDownload(
    () => import("../components/CompanyProfilePdf"),
    "ZenFresh-Company-Profile.pdf",
    "company_profile_download"
  );

  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={12} />
        <JumpingFish className="left-[9%] bottom-10 hidden sm:block" size={40} delay={0.4} />
        <JumpingFish
          className="right-[16%] bottom-16 hidden sm:block"
          size={28}
          delay={2.4}
          duration={2.1}
          color="var(--color-coral-400)"
          flip
        />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Our Story
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Built for the fishermen. Built for you.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            ZenFresh exists to shorten the distance between the boat and your kitchen —
            for fresher food and fairer livelihoods.
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      {/* Narrative + founder illustration */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:items-center">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-ocean-950 sm:text-3xl">
              Seafood supply chains were built for warehouses, not for people.
            </h2>
            <p className="mt-5 leading-relaxed text-ocean-800/70">
              We started ZenFresh after watching a friend&apos;s family fishing business struggle — not for
              lack of a good catch, but because of layers of distributors that took most of the margin and
              days off the freshness. Somewhere between the dock and the dinner plate, seafood was losing
              both its quality and its story.
            </p>
            <p className="mt-4 leading-relaxed text-ocean-800/70">
              So we cut it down to two links: the fisherman who caught it, and you. Every order on ZenFresh
              ships within hours of landing, packed in ice by the same crew that caught it, and arrives with
              a record of exactly which boat it came from.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={0.1} className="mx-auto w-full max-w-sm">
            <div className="rounded-2xl bg-ocean-900 p-8 text-center text-foam-50 ring-1 ring-ocean-900/10">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-teal-400/20 text-teal-300">
                <Anchor className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <p className="mt-5 font-display text-base leading-snug">{FOUNDER_STORY_CAPTION}</p>
            </div>
          </ScrollReveal>
        </div>

        {/* Mission / Vision */}
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          <ScrollReveal className="rounded-2xl border-l-4 border-teal-400 bg-foam-100 p-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-teal-600">Mission</span>
            <p className="mt-2 font-display text-lg leading-snug text-ocean-950">{MISSION}</p>
          </ScrollReveal>
          <ScrollReveal delay={0.08} className="rounded-2xl border-l-4 border-coral-400 bg-foam-100 p-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-coral-500">Vision</span>
            <p className="mt-2 font-display text-lg leading-snug text-ocean-950">{VISION}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* Fishermen */}
      <section className="relative overflow-hidden bg-ocean-900 py-24 text-foam-50">
        <Bubbles count={10} className="opacity-60" />
        <JumpingFish className="right-[8%] top-8 hidden lg:block" size={34} delay={1.4} duration={2.3} />
        <div className="relative mx-auto max-w-6xl px-6">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">Meet the Fishermen</h2>
            <p className="mt-4 text-foam-100/70">
              A handful of the crews behind your next meal.
            </p>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FISHERMEN.map((f, i) => (
              <ScrollReveal key={f.name} delay={i * 0.08}>
                <div className="rounded-2xl bg-white/5 p-6 text-center ring-1 ring-white/10">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-400/20 font-display text-lg font-bold text-teal-300">
                    {f.initials}
                  </div>
                  <h3 className="mt-4 font-display font-semibold">{f.name}</h3>
                  <p className="mt-1 text-sm text-foam-100/60">{f.region}</p>
                  <p className="text-xs text-teal-300/80">{f.years} fishing</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <ScrollReveal className="text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Milestones</h2>
          <p className="mt-4 text-ocean-800/70">Tap a milestone to see what happened.</p>
        </ScrollReveal>

        <div className="mt-16">
          <TimelineTrack milestones={TIMELINE} />
        </div>
      </section>

      {/* Leadership */}
      <section className="relative overflow-hidden bg-ocean-900 py-24 text-foam-50">
        <Bubbles count={8} className="opacity-50" />
        <div className="relative mx-auto max-w-6xl px-6">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">Leadership</h2>
            <p className="mt-4 text-foam-100/70">The people steering ZenFresh.</p>
          </ScrollReveal>

          <div className="mt-12">
            <LeadershipGrid />
          </div>
        </div>
      </section>

      {/* Company profile download */}
      <section className="bg-foam-50 py-16">
        <ScrollReveal className="mx-auto flex max-w-2xl flex-col items-center gap-4 px-6 text-center">
          <h2 className="font-display text-xl font-bold text-ocean-950 sm:text-2xl">
            Need it on paper?
          </h2>
          <p className="text-sm text-ocean-800/70">
            Download a print-ready company profile — mission, key metrics, compliance summary, and every
            city we deliver to.
          </p>
          <button
            type="button"
            onClick={handleDownloadProfile}
            disabled={pdfStatus === "generating"}
            className="mt-2 inline-flex items-center gap-2 rounded-full bg-teal-400 px-6 py-3 text-sm font-semibold text-ocean-950 transition-transform hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
          >
            <Download className="h-4 w-4" />
            {pdfStatus === "generating" ? "Preparing PDF…" : "Download Company Profile"}
          </button>
          {pdfStatus === "error" && (
            <p className="text-xs text-coral-500">Couldn&apos;t generate the PDF — please try again.</p>
          )}
        </ScrollReveal>
      </section>

      {/* Core Values */}
      <section className="bg-foam-100 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Our Core Values</h2>
            <p className="mt-4 text-ocean-800/70">Hover or tap a card to see it in practice.</p>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CORE_VALUES.map((value, i) => (
              <ScrollReveal key={value.title} delay={i * 0.08}>
                <ValueCard value={value} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
